import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import tripsService from "../../services/trips.service";
import {
  TripResponse,
  TripStopResponse,
  VehicleStopResponse,
  VehicleTripResponse,
} from "types/trip.types";

interface TripsState {
  trips: VehicleTripResponse[] | undefined;
  unassignedTrips: TripResponse[];
  unassignedStops: TripStopResponse[];
  loading: boolean;
  error: string | null;
}

const initialState: TripsState = {
  trips: undefined,
  unassignedTrips: [],
  unassignedStops: [],
  loading: false,
  error: null,
};

export const fetchTrips = createAsyncThunk(
  "trips/fetchTrips",
  async (params: {
    date: Date;
    page?: number;
    pageSize?: number;
    search?: string;
  }) => {
    const response = await tripsService.getTrips(params);
    return response;
  }
);

// Fetch unassigned trips thunk
export const fetchUnassignedTrips = createAsyncThunk(
  "trips/fetchUnassignedTrips",
  async (params: { date: Date }) => {
    const response = await tripsService.getUnassignedTrips(params);
    return response;
  }
);

// Fetch unassigned stops thunk
export const fetchUnassignedStops = createAsyncThunk(
  "trips/fetchUnassignedStops",
  async (params: { date: Date }) => {
    const response = await tripsService.getUnassignedStops(params);
    return response;
  }
);

const tripsSlice = createSlice({
  name: "trips",
  initialState,
  reducers: {
    resetTripsState: (state) => {
      state.trips = [];
      state.unassignedTrips = [];
      state.unassignedStops = [];
      state.loading = false;
      state.error = null;
    },
    updateVehicleStop: (
      state,
      action: {
        payload: {
          vehicleStopId: number;
          updatedStop: Partial<Omit<VehicleStopResponse, "vehicleStopId">>;
        };
      }
    ) => {
      const { vehicleStopId, updatedStop } = action.payload;
      if (!state.trips) return;
      for (const trip of state.trips) {
        const stopIdx = trip.vehicleStops?.findIndex(
          (stop) => stop.vehicleStopId === vehicleStopId
        );
        if (stopIdx !== undefined && stopIdx > -1 && trip.vehicleStops) {
          trip.vehicleStops[stopIdx] = {
            ...trip.vehicleStops[stopIdx],
            ...updatedStop,
          };
          break;
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTrips.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTrips.fulfilled, (state, action) => {
        state.loading = false;
        state.trips = action.payload.data ?? [];
      })
      .addCase(fetchTrips.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch trips";
      })
      // fetchUnassignedTrips
      .addCase(fetchUnassignedTrips.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUnassignedTrips.fulfilled, (state, action) => {
        state.loading = false;
        state.unassignedTrips = action.payload.data ?? [];
      })
      .addCase(fetchUnassignedTrips.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message || "Failed to fetch unassigned trips";
      })
      // fetchUnassignedStops
      .addCase(fetchUnassignedStops.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUnassignedStops.fulfilled, (state, action) => {
        state.loading = false;
        state.unassignedStops = action.payload ?? [];
      })
      .addCase(fetchUnassignedStops.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message || "Failed to fetch unassigned stops";
      });
  },
});

export const { resetTripsState, updateVehicleStop } = tripsSlice.actions;
export default tripsSlice.reducer;
